/**
 * `pending-deeper-jira` — wire contracts for the rewritten Jira hook.
 *
 * Two surfaces:
 *   1. Admin dry-run preview — POST /api/admin/jira/dry-run with a
 *      run_id returns the FULL payload the hook WOULD send (without
 *      actually sending). The admin can sanity-check label / sprint /
 *      assignee resolution before flipping `jira.enabled` to ON.
 *   2. Admin audit list — GET /api/admin/jira/links?flow_id= returns
 *      the `jira_issue_links` rows so operators can see which flow
 *      is bound to which Jira ticket.
 *
 * The hook itself is internal (worker → service); it has no API
 * surface. We DON'T expose the ADF builder over HTTP.
 */

import { z } from 'zod';

/** ADF (Atlassian Document Format) is JSON shaped like `{ type, content }`.
 *  We don't validate the full ADF tree on the wire — the hook produces
 *  it, the dry-run just passes it through. So we expose a recursive
 *  `z.any()` for the wire and tighten in tests. */
export const AdfNodeSchema: z.ZodType<unknown> = z.lazy(() =>
  z
    .object({
      type: z.string(),
      attrs: z.record(z.string(), z.unknown()).optional(),
      content: z.array(AdfNodeSchema).optional(),
      text: z.string().optional(),
      marks: z.array(z.record(z.string(), z.unknown())).optional(),
    })
    .passthrough(),
);

export const JiraDryRunRequestSchema = z.object({
  run_id: z.string().uuid(),
});
export type JiraDryRunRequest = z.infer<typeof JiraDryRunRequestSchema>;

/** What the hook would send to Jira's POST /rest/api/3/issue. */
export const JiraIssuePayloadSchema = z.object({
  projectKey: z.string(),
  summary: z.string(),
  issueType: z.string(),
  labels: z.array(z.string()),
  priority: z.string().nullable(),
  componentName: z.string().nullable(),
  sprintId: z.number().nullable(),
  epicKey: z.string().nullable(),
  assigneeAccountId: z.string().nullable(),
  description: AdfNodeSchema,
  attachments: z.array(
    z.object({
      filename: z.string(),
      mimeType: z.string(),
      sizeBytes: z.number().int().nonnegative(),
      source: z.string(),
    }),
  ),
});
export type JiraIssuePayload = z.infer<typeof JiraIssuePayloadSchema>;

export const JiraDryRunResponseSchema = z.object({
  /** Either CREATE (no existing open link) or COMMENT (dedup window hit). */
  action: z.enum(['create', 'comment', 'noop']),
  /** When action === 'comment', the issue key the comment would land on. */
  existingIssueKey: z.string().nullable(),
  payload: JiraIssuePayloadSchema.nullable(),
  /** Resolution metadata the operator can sanity-check. */
  diagnostics: z.object({
    severity: z.string(),
    flowTags: z.array(z.string()),
    matchedComponent: z.string().nullable(),
    matchedPriority: z.string().nullable(),
    matchedEpic: z.string().nullable(),
    activeSprintId: z.number().nullable(),
    duplicateOf: z.string().nullable(),
    rateLimited: z.boolean(),
  }),
});
export type JiraDryRunResponse = z.infer<typeof JiraDryRunResponseSchema>;

export const JiraIssueLinkSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  flowId: z.string().uuid().nullable(),
  jiraIssueKey: z.string(),
  jiraIssueId: z.string().nullable(),
  jiraStatus: z.string().nullable(),
  firstOpenedAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  lastRunId: z.string().uuid().nullable(),
  consecutivePasses: z.number().int().nonnegative(),
  linkStatus: z.enum(['open', 'closed', 'resolved-by-pass']),
});
export type JiraIssueLink = z.infer<typeof JiraIssueLinkSchema>;

export const JiraIssueLinksListResponseSchema = z.object({
  links: z.array(JiraIssueLinkSchema),
});
export type JiraIssueLinksListResponse = z.infer<typeof JiraIssueLinksListResponseSchema>;

/* ============================================================== */
/*  pending-deeper-jira v1.1 — run-page + force-open + close-link   */
/* ============================================================== */

/**
 * `GET /api/admin/jira/links` accepts an optional `run_id` query in
 * v1.1 so the run-page Jira tab can ask for the links that match a
 * specific run id (via the underlying `last_run_id` column or the
 * canonical run that created the link).
 *
 * No new schema needed — same response shape — but we expose a typed
 * filter to keep the call-site honest.
 */
export const JiraIssueLinksListQuerySchema = z.object({
  flow_id: z.string().uuid().optional(),
  run_id: z.string().uuid().optional(),
});
export type JiraIssueLinksListQuery = z.infer<typeof JiraIssueLinksListQuerySchema>;

/**
 * `POST /api/admin/jira/force-open` — bypass the dedup window and
 * file a fresh Jira issue for an explicit run. Used by the run-page
 * "Force open new issue" CTA.
 */
export const JiraForceOpenRequestSchema = z.object({
  run_id: z.string().uuid(),
});
export type JiraForceOpenRequest = z.infer<typeof JiraForceOpenRequestSchema>;

export const JiraForceOpenResponseSchema = z.object({
  action: z.enum(['created', 'skipped']),
  issueKey: z.string().nullable(),
  attachmentsUploaded: z.number().int().nonnegative(),
});
export type JiraForceOpenResponse = z.infer<typeof JiraForceOpenResponseSchema>;

/**
 * `POST /api/admin/jira/links/:id/close` — flip a Rynora-side link
 * to `closed` (does NOT transition the Jira issue itself).
 */
export const JiraCloseLinkResponseSchema = z.object({
  ok: z.boolean(),
  issueKey: z.string().nullable(),
});
export type JiraCloseLinkResponse = z.infer<typeof JiraCloseLinkResponseSchema>;
